import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { NotificacaoPedidoPopup } from "../components/NotificacaoPedidoPopup";
import { PEDIDOS_PENDENTES_LOCADOR_MOCK } from "../mocks/pedidosLocador";
import { FRETE_DISPONIVEL_MOCK } from "../mocks/freteDisponivel";
import { api } from "../api/client";
import { useToast } from "./ToastContext";
import { useNotificacaoSom } from "../hooks/useNotificacaoSom";
import { usePedidosRealtime } from "../hooks/usePedidosRealtime";
import { useOfertaFreteiro } from "../hooks/useOfertaFreteiro";

// HUB GLOBAL DE CHAMADOS (estilo Uber/iFood).
//
// Antes este provider só sabia disparar o popup em modo DEMO — pedidos reais
// nunca abriam o "chamado". E o som/oferta do freteiro só rodavam DENTRO de
// telas específicas (HomeLocador / HomeFreteiro), então se o usuário estivesse
// em qualquer outra tela, NADA tocava ("às vezes não chega / às vezes não chama").
//
// Agora a escuta vive aqui, no nível do app, acima de todas as telas:
//   • LOCADOR  → detecta pedido novo 'aguardando_locador' (realtime + polling)
//                e abre o popup de chamada + som em QUALQUER tela.
//   • FRETEIRO → enquanto online, escuta oferta-pendente e, ao chegar, toca o
//                som e navega pro chamado (estilo Uber) em QUALQUER tela.
//
// Para não duplicar com o que as telas já fazem, suprimimos o chamado quando o
// usuário JÁ está na tela do assunto (locador em 'pedidos_locador', freteiro em
// 'frete_disponivel') — essas telas têm sua própria UI dedicada.

export const NotificacoesPedidoContext = createContext(null);
export const useNotificacoesPedido = () => useContext(NotificacoesPedidoContext);

// ── Detecção de abertura via PUSH pra tela do chamado do freteiro ──────────
// O service worker abre o app em /?tela=frete_disponivel quando o freteiro
// clica na notificação. O App.js consome o ?tela= e LIMPA a URL antes da tela
// (lazy) montar — então capturamos aqui, num módulo eager que roda no boot.
// TelaFreteDisponivel consome a flag pra diferenciar "aguardando fretes" de
// "essa corrida já foi passada pra outro motorista" (notificação fantasma).
// O atalho do PWA (?source=pwa-shortcut) NÃO conta como push.
let _abriuViaPushFrete = false;
try {
  const _qs = new URLSearchParams(window.location.search);
  _abriuViaPushFrete = _qs.get("tela") === "frete_disponivel" && _qs.get("source") !== "pwa-shortcut";
} catch {}
export const consumirAberturaPushFrete = () => {
  const veio = _abriuViaPushFrete;
  _abriuViaPushFrete = false; // consome 1x — visitas manuais depois não herdam a flag
  return veio;
};

// Janela do "toque" do chamado do locador (overlay). Curto de propósito: é um
// alerta de chamada, não o cronômetro de 30min — esse continua na lista/banner.
const TOQUE_LOCADOR_SEG = 45;

// Cadência do toque insistente da oferta do freteiro (estilo Uber): repete a
// voz + vibração a cada 4s enquanto a MESMA oferta seguir pendente, com teto
// de 60s (= TTL da oferta no backend).
const TOQUE_OFERTA_INTERVALO_MS = 4000;
const TOQUE_OFERTA_MAX_MS = 60000;

// Mapeia um pedido cru da API /pedidos/ pro shape que o popup espera.
const mapLocadorParaPopup = (p) => ({
  id: p?.numero || p?.id?.slice(0, 8) || "novo",
  produto: { nome: p?.itens?.[0]?.nome_equipamento || "Equipamento", img: null },
  dias: p?.itens?.[0]?.dias || p?.dias || 1,
  cliente: { nome: p?.cliente_dados?.nome || "Cliente" },
  total: parseFloat(p?.valor_total) || 0,
  caucao: parseFloat(p?.valor_caucao) || 0,
});

export const NotificacoesPedidoProvider = ({ children, perfilAtivo, nav, user, tela }) => {
  const toast = useToast();  // fora do ToastProvider cai no alert() (ver useToast)
  const [notificacaoAtiva, setNotificacaoAtiva] = useState(null);
  const [pedidosNotificados, setPedidosNotificados] = useState(new Set());
  const som = useNotificacaoSom();

  // ───────────────────────────────────────────────────────────────
  // LOCADOR — chamado global de pedido novo
  // ───────────────────────────────────────────────────────────────
  const ehLocador = perfilAtivo === "locador";
  const idsVistosRef = useRef(new Set()); // pedidos já conhecidos (anti-repetição)
  const primeiroLoadRef = useRef(true);
  const telaRef = useRef(tela);
  useEffect(() => { telaRef.current = tela; }, [tela]);

  const checarPedidosLocador = useCallback(async () => {
    if (!ehLocador || !user?.id) return;
    let lista;
    try {
      lista = await api.listarPedidos();
    } catch {
      return; // rede falhou — tenta de novo no próximo ciclo
    }
    if (!Array.isArray(lista)) return;

    const pendentes = lista.filter(
      (p) => p.locador_id === user.id && p.status === "aguardando_locador"
    );
    const novos = pendentes.filter((p) => !idsVistosRef.current.has(p.id));
    pendentes.forEach((p) => idsVistosRef.current.add(p.id));

    // Primeiro carregamento só popula o "já visto" — não toca (senão tocaria
    // pra pedidos antigos sempre que o app abre).
    if (primeiroLoadRef.current) {
      primeiroLoadRef.current = false;
      return;
    }
    if (novos.length === 0) return;
    // Se o locador já está na aba de confirmação, ela cuida do alerta.
    if (telaRef.current === "locacoes_central") return;
    // Não reabre um chamado já dispensado/respondido.
    const alvo = novos.find((p) => !pedidosNotificados.has(p.id));
    if (!alvo) return;

    try { som.tocar("oferta", 0.4); } catch {}
    setNotificacaoAtiva({ tipo: "locador", pedido: mapLocadorParaPopup(alvo), realId: alvo.id });
  }, [ehLocador, user?.id, pedidosNotificados, som]);

  // Realtime + polling 8s (só ativo quando o perfil é locador).
  usePedidosRealtime({
    userId: ehLocador ? user?.id : null,
    papel: "locador",
    onChange: checarPedidosLocador,
    pollMs: ehLocador ? 8000 : 0,
  });

  // Reseta a deteção ao trocar de perfil/usuário.
  useEffect(() => {
    idsVistosRef.current = new Set();
    primeiroLoadRef.current = true;
  }, [perfilAtivo, user?.id]);

  // ───────────────────────────────────────────────────────────────
  // FRETEIRO — chamado global de oferta (estilo Uber)
  // ───────────────────────────────────────────────────────────────
  const ehFreteiro = perfilAtivo === "freteiro";

  // NÃO gateamos a escuta de ofertas pelo status online cacheado (poll de 20s):
  // ele atrasava — o freteiro só via a corrida depois de desligar/ligar (o
  // toggle forçava o re-sync). Agora escutamos /entregas/oferta-pendente
  // SEMPRE que o perfil é freteiro. É seguro: o backend só cria oferta pra
  // freteiro ONLINE, então offline o endpoint devolve null (sem corrida falsa).
  // Resultado: assim que o pagamento confirma e a oferta nasce, ela aparece
  // em ~1,5s — sem precisar mexer no online/offline.

  // Toque INSISTENTE da oferta (estilo Uber): voz + vibração repetindo a cada
  // ~4s enquanto a mesma oferta continuar pendente. Para quando a oferta some
  // do poll (aceita/recusada/expirada — o effect abaixo detecta), ou após 60s.
  // Interval/timeout vivem em ref e são SEMPRE limpos (unmount inclusive).
  const toqueOfertaRef = useRef({ interval: null, timeout: null, ofertaId: null });

  const pararToqueOferta = useCallback(() => {
    const t = toqueOfertaRef.current;
    if (t.interval) { clearInterval(t.interval); t.interval = null; }
    if (t.timeout) { clearTimeout(t.timeout); t.timeout = null; }
    t.ofertaId = null;
  }, []);

  const iniciarToqueOferta = useCallback((ofertaId) => {
    pararToqueOferta();
    toqueOfertaRef.current.ofertaId = ofertaId;
    const tocarCiclo = () => {
      try { som.tocar("oferta_voz", 0.4); } catch {}
      try { navigator.vibrate?.([300, 150, 300]); } catch {}
    };
    tocarCiclo(); // primeiro toque imediato
    toqueOfertaRef.current.interval = setInterval(tocarCiclo, TOQUE_OFERTA_INTERVALO_MS);
    toqueOfertaRef.current.timeout = setTimeout(pararToqueOferta, TOQUE_OFERTA_MAX_MS);
  }, [som, pararToqueOferta]);

  // Limpa o toque no unmount do provider (troca de sessão, hot reload).
  useEffect(() => pararToqueOferta, [pararToqueOferta]);

  const aoChegarOfertaFreteiro = useCallback(({ oferta, entrega, pedido }) => {
    try { iniciarToqueOferta(oferta?.id); } catch {}
    // Se o freteiro já está na tela do chamado, ela cuida da UI.
    if (telaRef.current === "frete_disponivel") return;
    const cand = (entrega?.candidatos_freteiros || []).find((c) => c.id === oferta.freteiro_id);
    nav("frete_disponivel", {
      oferta_id: oferta.id,
      id: entrega?.id,
      entrega_id: entrega?.id,
      valorFrete: entrega?.valor_frete || 0,
      origem: {
        nome: entrega?.origem?.cidade || "Coleta",
        endereco: [entrega?.origem?.logradouro, entrega?.origem?.numero, entrega?.origem?.bairro].filter(Boolean).join(", "),
        lat: parseFloat(entrega?.origem?.latitude ?? entrega?.origem?.lat),
        lng: parseFloat(entrega?.origem?.longitude ?? entrega?.origem?.lng),
      },
      destino: {
        nome: entrega?.destino?.cidade || "Entrega",
        endereco: [entrega?.destino?.rua, entrega?.destino?.numero, entrega?.destino?.bairro].filter(Boolean).join(", "),
        lat: parseFloat(entrega?.destino?.latitude ?? entrega?.destino?.lat),
        lng: parseFloat(entrega?.destino?.longitude ?? entrega?.destino?.lng),
      },
      distanciaTotal: cand ? (cand.distancia_metros / 1000).toFixed(1) : (entrega?.distancia_km || 0),
      pesoTotal: entrega?.peso_total_kg || 0,
      volumeM3: entrega?.volume_m3 || 0,
      tempoEstimado: Math.round((cand?.distancia_metros || 5000) / 500),
      itens: (pedido?.itens || []).map((i) => ({
        nome: i.nome_equipamento,
        qty: i.quantidade || 1,
        peso: i.peso_kg || i.equipamento?.peso_kg || 0,
        comprimento_cm: i.dimensoes?.comprimento_cm || i.equipamento?.dimensoes?.comprimento_cm,
        largura_cm: i.dimensoes?.largura_cm || i.equipamento?.dimensoes?.largura_cm,
        altura_cm: i.dimensoes?.altura_cm || i.equipamento?.dimensoes?.altura_cm,
      })),
      veiculo_minimo: entrega?.veiculo_minimo,
      expira_em: oferta.expira_em,
    });
  }, [nav, iniciarToqueOferta]);

  const { oferta: pollOfertaFreteiro } = useOfertaFreteiro({
    ativo: ehFreteiro,
    onNova: aoChegarOfertaFreteiro,
  });

  // Para o toque insistente assim que a oferta que está tocando some do poll
  // (freteiro aceitou/recusou em qualquer tela, TTL expirou ou corrida foi
  // pra outro motorista). O poll roda a cada ~1,5s, então o silêncio vem
  // quase imediato após a ação.
  useEffect(() => {
    const idPendente = pollOfertaFreteiro?.oferta?.id || null;
    const idTocando = toqueOfertaRef.current.ofertaId;
    if (idTocando && idTocando !== idPendente) pararToqueOferta();
  }, [pollOfertaFreteiro, pararToqueOferta]);

  // ───────────────────────────────────────────────────────────────
  // Ações do popup do LOCADOR
  // ───────────────────────────────────────────────────────────────
  const marcarRespondido = (id) => {
    if (id) setPedidosNotificados((prev) => new Set([...prev, id]));
  };

  const aceitarPedido = async () => {
    const id = notificacaoAtiva?.realId;
    setNotificacaoAtiva(null);
    marcarRespondido(id);
    if (id) {
      try { await api.aceitarPedido(id); }
      catch (e) { toast.erro(e?.message || 'Não foi possível aceitar o pedido.'); }
    }
    nav('locacoes_central', { aba: 'atividade' });
  };

  const recusarPedido = async () => {
    const id = notificacaoAtiva?.realId;
    setNotificacaoAtiva(null);
    marcarRespondido(id);
    if (id) {
      try { await api.recusarPedido(id, "Indisponibilidade"); }
      catch (e) { toast.erro(e?.message || 'Não foi possível recusar o pedido.'); }
    }
  };

  const verDetalhes = () => {
    const id = notificacaoAtiva?.realId;
    marcarRespondido(id);
    setNotificacaoAtiva(null);
    nav('locacoes_central', { aba: 'atividade' });
  };

  // Timeout do toque: só fecha o overlay. NÃO recusa o pedido — ele segue
  // pendente na lista/banner (e no cronômetro real de 30min do backend).
  const dispensarChamado = () => {
    marcarRespondido(notificacaoAtiva?.realId);
    setNotificacaoAtiva(null);
  };

  // Demo (mantido pra testes locais)
  const dispararNotificacaoDemo = (tipo) => {
    if (tipo === "locador") {
      setNotificacaoAtiva({ tipo: "locador", pedido: PEDIDOS_PENDENTES_LOCADOR_MOCK[0] });
    } else {
      setNotificacaoAtiva({ tipo: "freteiro", pedido: FRETE_DISPONIVEL_MOCK });
    }
  };

  return (
    <NotificacoesPedidoContext.Provider value={{
      notificacaoAtiva,
      aceitarPedido,
      recusarPedido,
      verDetalhes,
      dispararNotificacaoDemo,
      setPedidosNotificados,
    }}>
      {children}
      {/* Chamado global (overlay). Freteiro real navega pra tela dedicada, então
          o popup aqui é usado pro locador e pro demo. */}
      {notificacaoAtiva && (
        <NotificacaoPedidoPopup
          pedido={notificacaoAtiva.pedido}
          tipo={notificacaoAtiva.tipo}
          onAceitar={aceitarPedido}
          onRecusar={recusarPedido}
          onVerDetalhes={verDetalhes}
          onTimeout={notificacaoAtiva.tipo === "locador" ? dispensarChamado : undefined}
          tempoTotalSegundos={notificacaoAtiva.tipo === "locador" && notificacaoAtiva.realId ? TOQUE_LOCADOR_SEG : undefined}
        />
      )}
    </NotificacoesPedidoContext.Provider>
  );
};
